/**
 * Report Value / Inputs — MOCKUP (chờ chốt layout trước khi wire data thật).
 *
 * Convert từ Access `frmReportMain` (Navigation container):
 *   - Header: tiêu đề "Inputs" + Filter Date (start_date / end_date).
 *   - NavigationControl với 3 mục con:
 *       • Exposure Limit → frmReportExposureMain
 *       • Cash Flows      → frmReportCashFlowMain
 *       • PBI Report     → frmReportPBIMain
 *
 * Format page copy 1:1 từ Sale Contract page (PageHeader + filter trong actions
 * slot + Card chứa Tabs styled + CardContent). Tab đồng bộ URL (single source:
 * derive từ pathname, không useState song song):
 *   /report/inputs/exposure | /report/inputs/cashflows | /report/inputs/misa-mapping
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { Alert, Box, Card, CardContent, Stack, Tab, Tabs, Typography } from '@mui/material'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import dayjs, { type Dayjs } from 'dayjs'
import { useNavigate, useParams } from 'react-router-dom'
import { usePreference } from '@/hooks/usePreference'
import { REPORT_LAST_SECTION_KEY, REPORT_INPUTS_LAST_TAB_KEY } from './ReportIndexRedirect'
import PageHeader from '../../components/PageHeader'
import ReloadButton from '../../components/ReloadButton'
import { CARD_CONTENT_COMPACT, FILTER_HEADER_INPUT_SX } from '../../theme/responsive'
import ExposureLimitView from './exposure/ExposureLimitView'
import CashFlowInputsView from './cashflow/CashFlowInputsView'
import { CASHFLOW_HUB_PAGES, MISA_MAPPING_PAGES } from './cashflow/cashflowPages'
import ComingSoonPage from '@/pages/common/ComingSoonPage'
import { getUnbuild } from '../../config/pageBuildStatus'

type TabKey = 'exposure' | 'cashflows' | 'misa-mapping'

const TAB_CONFIG: { key: TabKey; label: string; blurb: string }[] = [
  {
    key: 'exposure',
    label: 'Exposure Limit',
    blurb: 'Exposure limits by spot month + the Limit (Approved) table. Edit each limit row, filtered by Filter Date.',
  },
  {
    key: 'cashflows',
    label: 'Cash Flows',
    blurb: 'Cash flow forecast inputs and the bank account balance report. Enter and edit values per period.',
  },
  {
    key: 'misa-mapping',
    label: 'Misa Mapping',
    blurb: 'MISA mapping: customer/vendor, service, and purchase/sale invoice mapping between ERP and MISA.',
  },
]

const compactInputSx = FILTER_HEADER_INPUT_SX

// Reload button format copied from Sale Contract (`headerButtonSx`).
const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none',
}

// Reload sits in the shared page header (block chung). Tags/endpoints switch
// with the active tab so it refetches the right list (mirrors the per-tab
// Reload that used to live inside each tab's toolbar).
const RELOAD_BY_TAB: Record<TabKey, { apiTags: string[]; watchEndpoints: string[] }> = {
  exposure: { apiTags: ['ReportExposure'], watchEndpoints: ['getExposureLimits'] },
  cashflows: {
    apiTags: ['ReportCashflow'],
    watchEndpoints: ['getCashflowInput', 'getCashflowBankBalance'],
  },
  'misa-mapping': {
    apiTags: ['ReportCashflow'],
    watchEndpoints: [
      'getCashflowMapCp',
      'getCashflowMapService',
      'getCashflowMapMuaHang',
      'getCashflowMapBanHang',
    ],
  },
}

export default function ReportValuePage() {
  const navigate = useNavigate()
  const { tab } = useParams<{ tab?: string }>()

  // Single source: tab active derive từ URL. Tab không tồn tại (link cũ đã xoá)
  // → quay về Home.
  const activeIndex = useMemo(() => {
    const t = (tab ?? '').toLowerCase()
    return TAB_CONFIG.findIndex((x) => x.key === t)
  }, [tab])

  useEffect(() => {
    if (activeIndex < 0) navigate('/home', { replace: true })
  }, [activeIndex, navigate])

  // Ghi nhớ SECTION = Inputs + tab con cuối (Exposure/Cash Flows/Misa) để `/report`
  // và `/report/inputs` lần sau mở lại đúng chỗ (xem ReportIndexRedirect). Chỉ set
  // khi đang ở tab Inputs hợp lệ.
  useEffect(() => {
    if (activeIndex < 0) return
    localStorage.setItem(REPORT_LAST_SECTION_KEY, 'inputs')
    localStorage.setItem(REPORT_INPUTS_LAST_TAB_KEY, TAB_CONFIG[activeIndex].key)
  }, [activeIndex])

  // Filter Date — scopes the Purchase/Sale invoice-mapping lists (muahang by
  // sohoadon_checkdate, banhang by contract_date), mirroring Access tmp_filter_date.
  // Persisted per-user via the generic registry (scope report_input / page_filter),
  // shared across all tabs. Baseline null → default [start-of-month, today].
  const dateFilter = usePreference<{ startDate: string | null; endDate: string | null }>(
    'report_input',
    'page_filter',
  )
  const [fromDate, setFromDate] = useState<Dayjs | null>(null)
  const [toDate, setToDate] = useState<Dayjs | null>(null)
  const hydrated = useRef(false)
  useEffect(() => {
    if (dateFilter.isLoading || hydrated.current) return
    hydrated.current = true
    setFromDate(dateFilter.value.startDate ? dayjs(dateFilter.value.startDate) : dayjs().startOf('month'))
    setToDate(dateFilter.value.endDate ? dayjs(dateFilter.value.endDate) : dayjs())
  }, [dateFilter])

  const dateRange = useMemo(
    () => ({
      startDate: fromDate?.isValid() ? fromDate.format('YYYY-MM-DD') : null,
      endDate: toDate?.isValid() ? toDate.format('YYYY-MM-DD') : null,
    }),
    [fromDate, toDate],
  )

  // Debounced persist when the range changes (after hydrate).
  // Deps = [dateRange] ONLY. `dateFilter` (usePreference) returns a NEW object
  // every render; including it here made the effect re-run each render, and since
  // patch optimistically updates the cache → re-render → new dateFilter ref →
  // effect → patch, it looped a PATCH /user-preferences every ~450ms. dateFilter
  // .patch is a stable useCallback, so omitting it is safe.
  useEffect(() => {
    if (!hydrated.current) return
    const t = setTimeout(() => {
      dateFilter.patch({ startDate: dateRange.startDate, endDate: dateRange.endDate }).catch(() => undefined)
    }, 450)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange])

  if (activeIndex < 0) return null
  const current = TAB_CONFIG[activeIndex]

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Inputs"
        module="Report"
        actions={
          <Stack
            direction="row"
            spacing={0.45}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Filter Date"
                format="DD-MMM-YYYY"
                value={fromDate}
                onChange={setFromDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: compactInputSx,
                    InputLabelProps: { shrink: true },
                    inputProps: { 'aria-label': 'Filter Date start' },
                  },
                }}
              />
              <ArrowRightAltIcon
                fontSize="small"
                sx={{ color: 'text.secondary', fontSize: { xs: 16, sm: 18 }, mx: 0.25 }}
                aria-hidden="true"
              />
              <DatePicker
                format="DD-MMM-YYYY"
                value={toDate}
                onChange={setToDate}
                slotProps={{
                  textField: {
                    size: 'small',
                    sx: compactInputSx,
                    inputProps: { 'aria-label': 'Filter Date end' },
                  },
                }}
              />
            </LocalizationProvider>
            <ReloadButton
              sx={headerButtonSx}
              apiTags={RELOAD_BY_TAB[current.key].apiTags}
              baseTags={[]}
              watchEndpoints={RELOAD_BY_TAB[current.key].watchEndpoints}
            />
          </Stack>
        }
      />
      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeIndex}
          onChange={(_, value: number) => navigate(`/report/inputs/${TAB_CONFIG[value].key}`)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {TAB_CONFIG.map((t) => (
            <Tab key={t.key} label={t.label} />
          ))}
        </Tabs>
        <CardContent sx={CARD_CONTENT_COMPACT}>
          {(() => {
            const unbuild = getUnbuild(`/report/inputs/${current.key}`)
            if (unbuild) {
              return <ComingSoonPage title={current.label} embedded status={unbuild.status} />
            }
            if (current.key === 'exposure') return <ExposureLimitView />
            // `key` per tab: cashflows & misa-mapping both render CashFlowInputsView
            // at the same tree slot → without a distinct key React reuses the
            // instance (pageKey/query state persist) and data doesn't reload on tab
            // switch. Keying forces unmount/remount → queries refetch fresh.
            if (current.key === 'misa-mapping') return <CashFlowInputsView key="misa-mapping" pages={MISA_MAPPING_PAGES} dateRange={dateRange} />
            return <CashFlowInputsView key="cashflows" pages={CASHFLOW_HUB_PAGES} dateRange={dateRange} />
          })()}
        </CardContent>
      </Card>
    </Box>
  )
}
